import React from 'react';
import { LegalLayout, H2, P, UL } from './LegalLayout';

/**
 * Privacy Policy — DRAFT scaffold for UK-childcare GDPR / UK-DPA.
 *
 * This is a starting point covering the categories the ICO expects
 * (lawful basis, data categories, retention, rights, transfers).
 * MUST be reviewed by a solicitor before commercial use.
 */

export default function PrivacyPolicy() {
  return (
    <LegalLayout
      title="Privacy Policy"
      description="How Kids WiseApp collects, uses, and protects personal data — including children's data — in compliance with UK GDPR and the Data Protection Act 2018."
      canonical="https://kidswiseapp.com/privacy"
      lastUpdated={new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}
    >
      <P>
        Kids WiseApp Ltd. ("<strong>we</strong>", "<strong>us</strong>")
        provides a multi-tenant childcare management platform used by
        nurseries, after-school clubs, holiday programs, and individual
        childminders. We take the privacy of children, parents, and
        staff extremely seriously and process personal data only in
        line with the UK General Data Protection Regulation (UK GDPR),
        the Data Protection Act 2018, and applicable safeguarding
        guidance from the ICO.
      </P>

      <H2>Who is the data controller?</H2>
      <P>
        For the personal data of <strong>parents and staff</strong> who
        register with a childcare provider on our platform, that
        provider (your nursery / after-school club / childminder) is
        the data <em>controller</em>. Kids WiseApp acts as the data
        <em>processor</em> under a written Data Processing Agreement
        (DPA). For platform-wide account data (billing, support emails,
        the website you're reading right now), Kids WiseApp Ltd. is
        the controller.
      </P>

      <H2>What we collect</H2>
      <UL>
        <li><strong>Account data</strong>: name, email, phone, role, organisation, password hash.</li>
        <li><strong>Children's data</strong>: name, date of birth, allergies, dietary needs, medical conditions, emergency contacts, school, transport requirements.</li>
        <li><strong>Operational data</strong>: bookings, attendance, sign-in/sign-out times, incident reports (including photos uploaded by staff), learning moments, billing transactions.</li>
        <li><strong>Communications data</strong>: messages between parents and staff, push notification subscriptions, email logs.</li>
        <li><strong>Technical data</strong>: IP address, browser fingerprint, device type, session timestamps (used for security, rate limiting, and audit logs).</li>
      </UL>

      <H2>Lawful bases for processing</H2>
      <UL>
        <li><strong>Contract</strong>: to deliver the childcare services parents have signed up for and that providers offer.</li>
        <li><strong>Legitimate interest</strong>: to run the platform, prevent abuse, and improve the service (e.g. error monitoring, security audits).</li>
        <li><strong>Legal obligation</strong>: safeguarding incident reporting, Ofsted record-keeping requirements, tax/accounting records.</li>
        <li><strong>Consent</strong>: optional features such as analytics cookies, marketing emails, or photo-sharing on the parent app.</li>
      </UL>

      <H2>Children's data — special protections</H2>
      <P>
        Because the platform processes data about children under 13, we
        apply the following extra safeguards: photos uploaded to ID
        cards must be approved by a manager before going live;
        incident report photos use cryptographically-unguessable URLs
        and are only shared via authenticated APIs; communications
        between staff and parents are logged and tenant-scoped; no
        children's data is sold, profiled for advertising, or shared
        with third parties without your provider's instruction.
      </P>

      <H2>How long we keep data</H2>
      <UL>
        <li>Active account data: while the account is active + 30 days after deactivation.</li>
        <li>Safeguarding incident records: <strong>21 years</strong> (in line with childcare safeguarding guidance).</li>
        <li>Financial / billing records: <strong>7 years</strong> (HMRC).</li>
        <li>Audit logs of admin actions: <strong>2 years</strong>.</li>
        <li>Marketing email logs: <strong>1 year</strong> after last contact.</li>
      </UL>

      <H2>Your rights</H2>
      <P>You can ask us to:</P>
      <UL>
        <li>access the personal data we hold about you (Subject Access Request)</li>
        <li>correct inaccurate data</li>
        <li>delete your data ("right to be forgotten") subject to safeguarding / legal retention obligations</li>
        <li>restrict or object to processing</li>
        <li>receive your data in a portable format</li>
        <li>withdraw any consent you previously gave</li>
      </UL>
      <P>
        To exercise any of these rights, email{' '}
        <a href="mailto:privacy@kidswiseapp.com">privacy@kidswiseapp.com</a>{' '}
        or use the in-app "Delete my account" function on your profile
        page. We respond within 30 days.
      </P>

      <H2>Sub-processors we use</H2>
      <UL>
        <li><strong>MongoDB Atlas</strong> (UK / EU region) — primary database.</li>
        <li><strong>IONOS S3</strong> — object storage for media uploads.</li>
        <li><strong>Mollie</strong> — subscription billing &amp; UK BACS Direct Debit (no card or bank-account data ever touches our servers).</li>
        <li><strong>one.com</strong> — transactional email (sign-up confirmations, password resets, incident notifications).</li>
        <li><strong>Emergent</strong> — application hosting and content delivery.</li>
      </UL>
      <P>All sub-processors are bound by a written DPA and process data only on our instructions.</P>

      <H2>International transfers</H2>
      <P>
        Our primary infrastructure is hosted in the UK / EU. Where any
        sub-processor processes data outside the UK, we rely on the UK
        International Data Transfer Agreement or the EU Standard
        Contractual Clauses with appropriate supplementary measures.
      </P>

      <H2>Contact</H2>
      <P>
        Questions or complaints can be sent to{' '}
        <a href="mailto:privacy@kidswiseapp.com">privacy@kidswiseapp.com</a>.
        You also have the right to complain to the UK Information
        Commissioner's Office (<a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">ico.org.uk</a>),
        though we'd appreciate the chance to address your concerns
        first.
      </P>
    </LegalLayout>
  );
}
